// Keeps one cast player on the shared timeline. Rules R1–R20 and cases C1–C25:
// docs/superpowers/plans/2026-10-12-cast-sync-web.md Task 3 (Android mirrors them).
// R21 (link only; cases C42–C49): while the timeline is PLAY, the player reports playing, no wake
// is pending and the cooldown is over, a drift with 150 < |drift| <= 300 of the SAME sign across
// consecutive playing reports spanning >= 3000 ms (first report of the run to the current one)
// seeks to expected + seekCost like R16. The run restarts on |drift| <= 150, a sign flip, any seek,
// a new/re-applied timeline, EVERY onClockChange (always, wake pending or not), a wake firing, a
// non-playing report, or a report inside the cooldown (those never count). Stops clients settling
// at different offsets inside the 300 ms band.
// R22 (cases C65, C65b; Android ports it verbatim): the item's end. END = durationMs - END_EPS_MS,
// durationMs being what the player reported for this media (null = unknown).
//  a) Duration known: every seek the follower would make on a PLAY timeline (R7, R8b, R15, R16,
//     R17, R21) whose expected position is >= END is instead "end": [pause, ended]; a seek whose
//     expected is below END goes to min(expected + seekCost, END), never past it.
//  b) Duration unknown, link only: a forward drift seek (R16/R21: issued while playing, its target
//     ms above the last reported position) arms restart detection with that target. The next
//     "playing" report, however late, disarms it and is a restart if position <=
//     RESTART_NEAR_ZERO_MS and position + RESTART_JUMP_MS < target (the provider went back to 0
//     instead of ending), else it resets the restart count to 0. The next seek, or a new or
//     re-applied timeline, also disarms it (a timeline also resets the count). RESTART_LOOP
//     consecutive restarts are "end" (checked before R13 learning); one alone is corrected normally.
//  "end" emits `ended` once per applied timeline (the caller treats it like the player's own
//  ENDED: your own cast advances, a viewer only holds). Until the next timeline the follower then
//  holds: a "playing" report returns [pause], other reports and wakes return [], and a resync
//  returns [pause]. A new timeline starts over.
import { positionAt, sameTimeline, type CastTimeline } from "./timeline";

export type CastKind = "file" | "link";
export type PlayerState = "playing" | "paused" | "buffering" | "ended";
export type FollowerCommand =
  | { type: "seek"; ms: number }
  | { type: "play" }
  | { type: "pause" }
  | { type: "rate"; rate: number }
  | { type: "wakeAt"; mono: number }
  /** R22: the media is over for this timeline; handle it like the player's own ENDED. */
  | { type: "ended" };

export const COOLDOWN_MS = 2000;
export const LEARN_DELAY_MS = 250;
export const LINK_SEEK_DRIFT_MS = 300;
export const LINK_STEADY_DRIFT_MS = 150;
export const LINK_STEADY_MS = 3000;
export const PAUSE_SNAP_DRIFT_MS = 300;
export const FILE_SEEK_DRIFT_MS = 1000;
export const FILE_NUDGE_DRIFT_MS = 80;
export const FILE_SETTLED_DRIFT_MS = 40;
export const RATE_SLOW = 0.95;
export const RATE_FAST = 1.05;
export const SEEK_COST_INIT: Record<CastKind, number> = { link: 400, file: 150 };
export const SEEK_COST_MAX = 2000;
export const END_EPS_MS = 500;
export const RESTART_NEAR_ZERO_MS = 1500;
export const RESTART_JUMP_MS = 1000;
export const RESTART_LOOP = 2;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export class CastFollower {
  /** The applied timeline (a PLAY without a start is anchored on receipt). */
  timeline: CastTimeline | null = null;
  seekCost: number;
  rate = 1;
  lastDrift: number | null = null;
  /** R22: the player's reported duration of this media; null while unknown. */
  durationMs: number | null = null;

  private received: CastTimeline | null = null;
  private cooldownUntil = -Infinity;
  private wakeAt: number | null = null;
  private learnFrom: number | null = null;
  /** R21: start (mono) and sign of the current steady link-drift run. */
  private steadyFrom: number | null = null;
  private steadySign = 0;
  /** R22: the timeline this media was ended for (held until a new timeline). */
  private endedFor: CastTimeline | null = null;
  /** R22b: the last reported position, the armed drift-seek target, and the run of restarts. */
  private lastPos: number | null = null;
  private restartTarget: number | null = null;
  private restarts = 0;

  constructor(readonly kind: CastKind) {
    this.seekCost = SEEK_COST_INIT[kind];
  }

  onTimeline(next: CastTimeline, mono: number, serverNow: number): FollowerCommand[] {
    const last = this.received;
    if (last && next.rev != null && last.rev != null && next.rev <= last.rev) return []; // R1
    if (last && next.rev == null && sameTimeline(last, next)) return []; // R2
    this.received = next;
    this.timeline =
      next.status === "PLAY" && next.startAt == null ? { ...next, startAt: serverNow } : next; // R5
    return this.apply(mono, serverNow);
  }

  /** R22: ended for the current timeline, holding at the end. */
  get heldAtEnd(): boolean {
    return this.timeline != null && this.endedFor === this.timeline;
  }

  resync(mono: number, serverNow: number): FollowerCommand[] {
    return this.timeline ? this.apply(mono, serverNow) : [];
  }

  /** The server clock estimate moved or the tab woke: only a pending scheduled start is recomputed (R8c). */
  onClockChange(mono: number, serverNow: number): FollowerCommand[] {
    this.steadyFrom = null; // R21: drift before and after the change is measured on different clocks
    return this.timeline && this.wakeAt != null ? this.apply(mono, serverNow) : [];
  }

  onWake(mono: number, serverNow: number): FollowerCommand[] {
    const t = this.timeline;
    if (this.wakeAt == null || t?.status !== "PLAY" || this.heldAtEnd) return [];
    if (mono < this.wakeAt) return [{ type: "wakeAt", mono: this.wakeAt }]; // R8
    const late = mono - this.wakeAt;
    this.wakeAt = null;
    this.steadyFrom = null; // R21
    // R8b: a throttled timer that fires late would start behind; catch up like a late join.
    if (late > (this.kind === "link" ? LINK_SEEK_DRIFT_MS : FILE_SEEK_DRIFT_MS)) {
      return this.seekTo(positionAt(t, serverNow), mono, true);
    }
    this.cooldownUntil = mono + COOLDOWN_MS; // R9
    return [{ type: "play" }];
  }

  onReport(positionMs: number, state: PlayerState, mono: number, serverNow: number): FollowerCommand[] {
    const t = this.timeline;
    if (!t || this.wakeAt != null || t.status === "PAUSE" || state !== "playing") this.steadyFrom = null; // R21
    if (!t || this.wakeAt != null) return []; // R10
    this.lastPos = positionMs;
    if (this.heldAtEnd) return state === "playing" ? [{ type: "pause" }] : []; // R22: hold until a new timeline
    if (t.status === "PAUSE") {
      // R11b: the timeline is paused but the player plays (media key, lost pause): stop it at once.
      if (state === "playing") {
        this.lastDrift = positionMs - t.seekMs;
        return [{ type: "pause" }, { type: "seek", ms: t.seekMs }];
      }
      const off = positionMs - t.seekMs;
      this.lastDrift = off;
      if (mono < this.cooldownUntil || state === "buffering") return [];
      if (Math.abs(off) <= PAUSE_SNAP_DRIFT_MS) return [];
      this.cooldownUntil = mono + COOLDOWN_MS; // R11
      return [{ type: "seek", ms: t.seekMs }];
    }
    if (state === "buffering") return []; // R12
    const expected = positionAt(t, serverNow);
    const drift = positionMs - expected;
    this.lastDrift = drift;
    const target = this.restartTarget;
    if (target != null && state === "playing") {
      this.restartTarget = null; // R22b: the next playing report decides
      if (positionMs <= RESTART_NEAR_ZERO_MS && positionMs + RESTART_JUMP_MS < target) {
        if (++this.restarts >= RESTART_LOOP) return this.end(); // the provider restarted at 0 instead of ending
      } else this.restarts = 0;
    }
    if (state === "playing" && this.learnFrom != null && mono >= this.learnFrom) {
      this.learnFrom = null; // R13
      this.seekCost = clamp(Math.round(0.7 * this.seekCost + 0.3 * (this.seekCost - drift)), 0, SEEK_COST_MAX);
    }
    if (mono < this.cooldownUntil) {
      this.steadyFrom = null; // R21: reports inside the cooldown never count
      return []; // R14
    }
    if (state === "ended") {
      // R15: the media just finished and the timeline is not clearly before its end: stay there.
      // Only a timeline clearly before the reported end (publisher re-anchored back) restarts it.
      const thr = this.kind === "link" ? LINK_SEEK_DRIFT_MS : FILE_SEEK_DRIFT_MS;
      if (expected >= positionMs - thr) return [];
      return this.seekTo(expected, mono, true);
    }
    if (state !== "playing") return this.seekTo(expected, mono, true); // R15 (paused)
    const a = Math.abs(drift);
    if (this.kind === "link") {
      if (a > LINK_SEEK_DRIFT_MS) return this.seekTo(expected, mono, false); // R16
      return this.steadyLink(drift, expected, mono); // R21
    }
    if (a > FILE_SEEK_DRIFT_MS) return [...this.resetRate(), ...this.seekTo(expected, mono, false)]; // R17
    if (a >= FILE_NUDGE_DRIFT_MS) {
      const want = drift > 0 ? RATE_SLOW : RATE_FAST; // R18
      if (want === this.rate) return [];
      this.rate = want;
      return [{ type: "rate", rate: want }];
    }
    if (a < FILE_SETTLED_DRIFT_MS) return this.resetRate(); // R19
    return []; // R20
  }

  private apply(mono: number, serverNow: number): FollowerCommand[] {
    const t = this.timeline!;
    this.wakeAt = null;
    this.learnFrom = null;
    this.steadyFrom = null; // R21
    this.restartTarget = null; // R22b
    this.restarts = 0;
    const out = this.resetRate(); // R3
    if (t.status === "PAUSE") {
      this.cooldownUntil = mono + COOLDOWN_MS; // R4
      return [...out, { type: "pause" }, { type: "seek", ms: t.seekMs }];
    }
    const startAt = t.startAt ?? serverNow;
    if (serverNow < startAt) {
      this.wakeAt = mono + (startAt - serverNow); // R6
      return [...out, { type: "pause" }, { type: "seek", ms: t.seekMs }, { type: "wakeAt", mono: this.wakeAt }];
    }
    return [...out, ...this.seekTo(positionAt(t, serverNow), mono, true)]; // R7
  }

  /** R21: a steady link drift inside the 300 ms band for LINK_STEADY_MS seeks like R16. */
  private steadyLink(drift: number, expected: number, mono: number): FollowerCommand[] {
    if (Math.abs(drift) <= LINK_STEADY_DRIFT_MS) {
      this.steadyFrom = null;
      return [];
    }
    const sign = Math.sign(drift);
    if (this.steadyFrom == null || sign !== this.steadySign) {
      this.steadyFrom = mono;
      this.steadySign = sign;
      return [];
    }
    return mono - this.steadyFrom >= LINK_STEADY_MS ? this.seekTo(expected, mono, false) : [];
  }

  private seekTo(expected: number, mono: number, play: boolean): FollowerCommand[] {
    this.steadyFrom = null; // R21
    this.restartTarget = null; // R22b
    const d = this.durationMs != null && this.durationMs > 0 ? this.durationMs : null;
    const endMs = d != null ? d - END_EPS_MS : null;
    if (this.heldAtEnd || (endMs != null && expected >= endMs)) return this.end(); // R22a
    this.cooldownUntil = mono + COOLDOWN_MS;
    this.learnFrom = mono + LEARN_DELAY_MS;
    let ms = Math.round(expected + this.seekCost);
    if (endMs != null) ms = Math.min(ms, Math.round(endMs)); // R22a: never past the end
    if (this.kind === "link" && endMs == null && !play && this.lastPos != null && ms > this.lastPos) {
      this.restartTarget = ms; // R22b
    }
    const seek: FollowerCommand = { type: "seek", ms };
    return play ? [seek, { type: "play" }] : [seek];
  }

  /** R22: hold at the end; `ended` only the first time for this timeline. */
  private end(): FollowerCommand[] {
    this.steadyFrom = null;
    this.restartTarget = null;
    if (this.heldAtEnd) return [{ type: "pause" }];
    this.endedFor = this.timeline;
    return [{ type: "pause" }, { type: "ended" }];
  }

  private resetRate(): FollowerCommand[] {
    if (this.rate === 1) return [];
    this.rate = 1;
    return [{ type: "rate", rate: 1 }];
  }
}
